import { Button, ButtonGroup } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import { config } from 'lib-shared-backend-config';
import { WidgetPresence } from 'lib-shared-legacy-types';
import { Component, CSSProperties, ReactElement } from 'react';


import AjaxLoader from '../../ajax/components/AjaxLoader';
import type {
  DemoWidgetDispatchPropsT,
  DemoWidgetStatePropsT,
} from '../containers/demoWidget';

import { iFramePropT, isIframe } from './modals/widgetcustomization';

type DemoWidgetOwnPropsT = {
  hideLanguageButtons?: boolean;
  widgetType: string;
};

export type PropsT = DemoWidgetOwnPropsT &
  DemoWidgetStatePropsT &
  DemoWidgetDispatchPropsT;

type StateT = {
  currentView: string | null | undefined;
  locale: string | null | undefined;
  show_widget: boolean;
  show_contact_form: boolean;
  loading: boolean;
  iframeStyle?: CSSProperties;
};

export default class DemoWidget extends Component<PropsT, StateT> {
  state = {
    currentView: null,
    locale: null,
    show_widget: false,
    show_contact_form: false,
    loading: true,
    iframeStyle: undefined,
  };

  componentDidMount() {
    this._buildWidget('demo-widget-iframe-wrapper-medium');
    this._buildWidget('demo-widget-iframe-wrapper-small');
    this._setupMessageListener();
  }

  componentWillUnmount() {
    window.removeEventListener('message', this._setupMessageListener, false);
  }

  @boundMethod
  sendToIframeSmall(iFrameProps: iFramePropT): void {
    if (document) {
      const ID = 'demo-widget-iframe-wrapper-small';
      this.sendToDixa(ID, iFrameProps);
    }
  }

  @boundMethod
  sendToIframeMedium(iFrameProps: iFramePropT): void {
    if (document) {
      const ID = 'demo-widget-iframe-wrapper-medium';
      this.sendToDixa(ID, iFrameProps);
    }
  }

  @boundMethod
  sendToDixa(id: string, iFrameProp: iFramePropT): void {
    const iframe = document.getElementById(id);

    if (isIframe(iframe) && iframe.contentWindow) {
      iframe.contentWindow._dixa(
        iFrameProp.apiText,
        iFrameProp.stateText,
        iFrameProp.targetValue,
        iFrameProp.contactText,
      );
    }
  }

  @boundMethod
  handleClickLanguage(value: string): void {
    const iFrameProps = {
      apiText: 'api.setLanguage',
      stateText: value,
    };

    this.sendToIframeMedium(iFrameProps);
    this.sendToIframeSmall(iFrameProps);
  }

  @boundMethod
  handleClickView(value: string): void {
    const iFrameProps = {
      apiText: 'api.setView',
      stateText: value,
    };

    this.sendToIframeMedium(iFrameProps);
    this.sendToIframeSmall(iFrameProps);
  }

  @boundMethod
  handleSetWidgetShowState(payload: WidgetPresence): void {
    const payloadString = JSON.stringify(payload);

    const iFrameProps = {
      apiText: 'api.showWidget',
      stateText: payloadString,
    };

    this.setState(payload);
    this.sendToIframeMedium(iFrameProps);
    this.sendToIframeSmall(iFrameProps);
  }

  _buildWidget(iframeID: string): void {
    // iframe-[ception]
    // We build a iframe "dixa-widget-wrapper" into the iframe "demo-widget-iframe-wrapper-[medium|small]" where we add our script loader.js
    const iframe = document.getElementById(iframeID);
    if (iframe && isIframe(iframe) && iframe.contentWindow) {
      (iframe.contentWindow.window['_dixa'] =
        iframe.contentWindow.window['_dixa'] ||
        function (...args) {
          if (iframe.contentWindow) {
            (iframe.contentWindow.window['_dixa'].q =
              iframe.contentWindow.window['_dixa'].q || []).push(args);
          }

          const widgetWrapper = iframe?.contentDocument?.getElementById(
            'dixa-widget-wrapper',
          );
          if (
            widgetWrapper &&
            isIframe(widgetWrapper) &&
            widgetWrapper.contentWindow
          ) {
            widgetWrapper.contentWindow.postMessage(JSON.stringify(args), '*');
          }
        }),
        (iframe.contentWindow.window['_dixa'].wid = this.props.widget_id);
      iframe.contentWindow.window['_dixa'].l = new Date().getTime();
      iframe.contentWindow.window['_dixa'].demo = true;

      if (this.props.settings !== null) {
        iframe.contentWindow.window['_dixa'].settings = this.props.settings;
      }
      iframe.contentWindow.document.open();
      // Stage tests.
      // http://stage-widget.dixa.io/assets/scripts/javascript/loader.js <-- Stage base URL !
      // iframe.contentWindow.window["BASE_PATH"] = "http://stage-widget.dixa.io/";

      // This should be safe to do.
      // eslint-disable-next-line no-unsanitized/method
      iframe.contentWindow.document.write(
        `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_parent" ><title>Dixa widget Demo</title><script type="text/javascript" charset="utf-8" src="${config.url.widget}/assets/scripts/javascript/loader.js" ></script></head><body><script>window.addEventListener("message", function(e) {console.info(e); window.top.postMessage(JSON.stringify(event), "*")}, false)</script></body></html>`,
      );
      iframe.contentWindow.document.close();
    }
  }

  @boundMethod
  handleWidgetTypeClick(widgettype: string): void {
    this.props.changeWidgetTypeShown(widgettype);
  }

  _setupMessageListener(): void {
    window.onmessage = (event) => {
      let dataObj;
      try {
        dataObj = JSON.parse(event.data);
      } catch (exception) {
        dataObj = event.data;
      }

      if (dataObj?.currentView && typeof dataObj.currentView === 'string') {
        this.setState(
          { currentView: dataObj.currentView, loading: false },
          () => {
            this.props.changeSelectedView(dataObj.currentView);
          },
        );
      }
      if (dataObj.locale && typeof dataObj.locale === 'string') {
        this.setState({ locale: dataObj.locale, loading: false }, () => {
          this.props.changeSelectedLanguage(dataObj.locale);
        });
      }

      if (dataObj.show_widget && typeof dataObj.show_widget === 'boolean') {
        this.setState(
          { show_widget: dataObj.show_widget, loading: false },
          () => {
            this.props.changeWidgetState({ show_widget: dataObj.show_widget });
          },
        );
      }

      if (
        dataObj.show_contact_form &&
        typeof dataObj.show_contact_form === 'boolean'
      ) {
        this.setState(
          { show_contact_form: dataObj.show_contact_form, loading: false },
          () => {
            this.props.changeWidgetState({
              show_contact_form: Boolean(dataObj?.show_contact_form),
            });
          },
        );
      }

      if (typeof dataObj === 'string' && dataObj === 'handshake') {
        const iFrameProps = {
          apiText: 'api.setLanguage',
          stateText: 'en-US',
        };

        this.sendToIframeMedium(iFrameProps);
        this.sendToIframeSmall(iFrameProps);

        const widget = document.getElementById('demo-widget');
        if (widget) {
          const demoWidgetWrapperWidth = widget.offsetWidth;
          if (demoWidgetWrapperWidth <= 1016) {
            this.setState({
              iframeStyle: { minWidth: `${demoWidgetWrapperWidth}px` },
            });
          }
        }
      }
    };
  }

  render() {
    let loading: null | ReactElement = null;
    let languageButtons: null | ReactElement = null;

    if (this.state.loading) {
      loading = (
        <div className="loading-overlay">
          <AjaxLoader text="Loading..." />
        </div>
      );
    }

    if (this.props.hideLanguageButtons) {
      languageButtons = (
        <ButtonGroup>
          <Button
            className="btn-primary"
            active={this.state.locale === 'da'}
            onClick={() => {
              this.handleClickLanguage('da');
            }}
          >
            Danish
          </Button>
          <Button
            className="btn-primary"
            active={this.state.locale === 'en-US'}
            onClick={() => {
              this.handleClickLanguage('en-US');
            }}
          >
            English
          </Button>
        </ButtonGroup>
      );
    }

    return (
      <div className="demo-widget" id="demo-widget">
        {loading}
        <div className="widget-buttons">
          <ButtonGroup>
            {languageButtons}

            <ButtonGroup>
              <Button
                active={
                  this.state.show_widget &&
                  this.state.show_contact_form === false
                }
                onClick={() => {
                  this.handleSetWidgetShowState({
                    show_widget: true,
                    show_contact_form: false,
                  });
                }}
              >
                Chat widget
              </Button>
              <Button
                active={this.state.show_widget && this.state.show_contact_form}
                onClick={() => {
                  this.handleSetWidgetShowState({
                    show_widget: true,
                    show_contact_form: true,
                  });
                }}
              >
                Contact widget
              </Button>
            </ButtonGroup>

            <ButtonGroup>
              <Button
                active={this.props.widgetType === 'medium'}
                onClick={() => {
                  this.handleWidgetTypeClick('medium');
                }}
              >
                Desktop
              </Button>
              <Button
                active={this.props.widgetType === 'small'}
                onClick={() => {
                  this.handleWidgetTypeClick('small');
                }}
              >
                Mobile
              </Button>
            </ButtonGroup>
          </ButtonGroup>
        </div>
        <div className="buttons hidden">
          <ButtonGroup>
            <ButtonGroup>
              <Button
                active={this.state.currentView === 'minimized'}
                onClick={() => {
                  this.handleClickView('minimized');
                }}
              >
                Minimized view
              </Button>
              <Button
                active={this.state.currentView === 'conversation'}
                onClick={() => {
                  this.handleClickView('conversation');
                }}
              >
                Conversation view
              </Button>
            </ButtonGroup>
          </ButtonGroup>
        </div>
        <iframe
          id="demo-widget-iframe-wrapper-medium"
          style={this.state.iframeStyle}
          className={classnames('demo-widget-iframe-wrapper-medium', {
            hidden: this.props.widgetType === 'small',
          })}
          scrolling="no"
        ></iframe>
        <div
          className={classnames('small-widget-wrapper', {
            hidden: this.props.widgetType === 'medium',
          })}
        >
          <iframe
            id="demo-widget-iframe-wrapper-small"
            className={classnames('demo-widget-iframe-wrapper-small', {
              hidden: this.props.widgetType === 'medium',
            })}
            scrolling="no"
          ></iframe>
        </div>
      </div>
    );
  }
}
